import React, { useState, useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useQuery } from 'react-query';
import { useSelector, useDispatch } from 'react-redux';
import { useRouteMatch, useHistory } from 'react-router-dom';
import ReactPaginate from 'react-paginate';
import styled from 'styled-components';
import {
  SPACING_PRODUCT_OPTIONS,
  ITEMS_PERPAGE_PRODUCT
} from 'styles/variables';
import ListProduct from './List/List';
import { RootState } from 'app/store';
import Title from 'components/ui-kits/Title';
import Sort from 'components/Sort';
import { SearchStyle, Wrapper, WrapperRight } from 'styles/listing-page';
import ListLayout from 'components/ListLayout';
import Button from 'components/ui-kits/Button';
import Loader from 'components/Loader';
import { PaginationWrapper } from 'styles/common';
import TextInput from 'components/ui-kits/Form/TextInput';
import { SearchIcon } from 'components/ui-kits/Icon';
import { statusPageProduct } from 'features/pagePagination/pagePaginationSlice';
import { getProducts } from 'services/productService';

const TableWrapper = styled.div`
  margin: ${SPACING_PRODUCT_OPTIONS.TABLE};
  overflow-x: auto;
`;

const Products = () => {
  const { merchantId } = useSelector((state: RootState) => state.user);
  const { currentPage, search } = useSelector(
    (state: RootState) => state.pagePagination
  );
  const dispatch = useDispatch();
  const history = useHistory();
  const { path } = useRouteMatch();
  const [pageCount, setPageCount] = useState(0);

  const {
    control,
    register,
    getValues,
    formState: { isDirty }
  } = useForm();

  const watchSearch = useWatch({
    control,
    defaultValue: search,
    name: 'search'
  });

  const params = {
    itemsPerPage: ITEMS_PERPAGE_PRODUCT,
    'merchant.id': merchantId,
    isOption: false,
    page: currentPage,
    'translations.name': search
  };

  const {
    isLoading: isLoadingProducts,
    isFetching,
    data: listProduct
  } = useQuery(
    ['getListProducts', { params }],
    ({ signal }) => getProducts({ signal, ...params }),
    { staleTime: Infinity }
  );

  useEffect(() => {
    if (listProduct?.data) {
      const totalItem: number = listProduct.data['hydra:totalItems'];
      setPageCount(Math.ceil(totalItem / ITEMS_PERPAGE_PRODUCT));
    }
  }, [listProduct]);

  useEffect(() => {
    if (!watchSearch && isDirty) {
      dispatch(
        statusPageProduct({
          currentPage: 1,
          search: ''
        })
      );
    }
  }, [watchSearch, getValues, dispatch, isDirty]);

  const handlePageClick = (event: any) => {
    const currPage = event.selected + 1;
    dispatch(
      statusPageProduct({
        currentPage: currPage,
        search: getValues('search')
      })
    );
  };

  const handleSearch = () => {
    dispatch(
      statusPageProduct({
        currentPage: 1,
        search: getValues('search')
      })
    );
  };

  return (
    <ListLayout>
      <Wrapper>
        <Title label="Produits" hasSearch={true} />
        <WrapperRight>
          <Sort />
          <SearchStyle>
            <TextInput
              register={register}
              name="search"
              placeholder="Rechercher un produit..."
              className="default__control"
              defaultValue={search}
            />
            <button
              className={`search-icon ${
                watchSearch?.length > 2 ? 'active' : 'disabled'
              }`}
              disabled={Boolean(!watchSearch || watchSearch?.length <= 2)}
              onClick={handleSearch}>
              <SearchIcon />
            </button>
          </SearchStyle>
          <div>
            <Button
              variant="primary"
              fontWeight="BOLD"
              onClick={() => history.push(`${path}/create`)}>
              + CRÉER UN PRODUIT
            </Button>
          </div>
        </WrapperRight>
      </Wrapper>
      <TableWrapper>
        {pageCount > 1 && (
          <PaginationWrapper>
            <ReactPaginate
              breakLabel="..."
              nextLabel=">"
              onPageChange={handlePageClick}
              pageCount={pageCount}
              previousLabel="<"
              pageRangeDisplayed={4}
              className="pagination"
              pageClassName="pagination-item"
              pageLinkClassName="pagination-link"
              activeLinkClassName="pagination-linkActive"
              forcePage={currentPage - 1}
            />
          </PaginationWrapper>
        )}
        {isLoadingProducts || isFetching ? (
          <Loader isFixed={false} />
        ) : (
          <ListProduct products={listProduct?.data?.['hydra:member']} />
        )}
      </TableWrapper>
    </ListLayout>
  );
};

export default Products;
